export default function ExportPanel({ kit }) {
  const cleanupSentence = (s:string) => {
    const t = String(s || '').trim().replace(/\s+/g, ' ');
    if (!t) return '';
    const noTrail = t.replace(/[.!?]+$/, '');
    const cap = noTrail.charAt(0).toUpperCase() + noTrail.slice(1);
    return `${cap}.`;
  };

  const starSummaryLine = (star:any) => {
    if (!star) return '';
    const res = cleanupSentence(star.result);
    const act = cleanupSentence(star.action);
    const task = cleanupSentence(star.task);
    const sit = cleanupSentence(star.situation);
    const metrics = String(star.metrics || '').trim();
    const parts:string[] = [];
    if (res) parts.push(res);
    if (act) parts.push(`Actions: ${act}`);
    if (task) parts.push(`Objective: ${task}`);
    if (sit) parts.push(`Context: ${sit}`);
    let line = parts.join(' ');
    if (metrics) line += ` (${metrics})`;
    return line;
  };

  const explicit = (kit?.achievements || []).map((a:any) => String(a?.bullet || '').trim()).filter(Boolean);
  const starSummaries = (kit?.star_entries || []).map(starSummaryLine).filter(Boolean);
  const seen = new Set<string>();
  const achievementsList = [...explicit, ...starSummaries].filter(b => {
    const key = b.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const md = [
    `## Summary`,
    ...((kit?.summary_variants || []).map((s:any) => `- (${s.tone}) ${s.text}`)),
    `\n## Keywords`,
    (kit?.keyword_bank || []).map((k:any) => k.term).join(", "),
    `\n## Achievements`,
    ...achievementsList.map(b => `- ${b}`),
    `\n## Skills`,
    ...((kit?.skills || []).map((s:any) => `- ${s.group}: ${s.name} (${s.level}/5)`)),
  ].join("\n");

  const copy = async () => {
    await navigator.clipboard.writeText(md);
    alert("Copied to clipboard!");
  };

  const exportAsPDF = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Resume Content Kit</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; }
            h1 { color: #333; border-bottom: 2px solid #0072CE; padding-bottom: 10px; }
            h2 { color: #0072CE; margin-top: 30px; }
            ul { margin: 10px 0; }
            li { margin: 5px 0; }
            .keywords { background: #f5f5f5; padding: 15px; border-radius: 5px; margin: 10px 0; }
            .achievement { margin: 8px 0; padding-left: 10px; }
          </style>
        </head>
        <body>
          <h1>Resume Content Kit</h1>
          
          <h2>Summary</h2>
          <ul>
            ${(kit?.summary_variants || []).map((s:any) => `<li><strong>${s.tone}:</strong> ${s.text}</li>`).join('')}
          </ul>
          
          <h2>Keywords</h2>
          <div class="keywords">
            ${(kit?.keyword_bank || []).map((k:any) => k.term).join(', ')}
          </div>
          
          <h2>Achievements</h2>
          <ul>
            ${achievementsList.map(b => `<li class="achievement">${b}</li>`).join('')}
          </ul>
          
          <h2>Skills</h2>
          <ul>
            ${(kit?.skills || []).map((s:any) => `<li><strong>${s.group}:</strong> ${s.name} (${s.level}/5)</li>`).join('')}
          </ul>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const exportAsWord = () => {
    const wordContent = `
RESUME CONTENT KIT
==================

SUMMARY
-------
${(kit?.summary_variants || []).map((s:any) => `• ${String(s.tone || '').toUpperCase()}: ${s.text}`).join('\n')}

KEYWORDS
--------
${(kit?.keyword_bank || []).map((k:any) => k.term).join(', ')}

ACHIEVEMENTS
------------
${achievementsList.map(b => `• ${b}`).join('\n')}

SKILLS
------
${(kit?.skills || []).map((s:any) => `• ${s.group}: ${s.name} (${s.level}/5)`).join('\n')}
    `.trim();

    const blob = new Blob([wordContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'resume-content-kit.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border rounded-xl p-6 space-y-4">
      <h3 className="font-semibold text-lg">Export Resume Content</h3>
      <p className="text-sm text-gray-600">Choose how you'd like to export your resume content:</p>
      
      <div className="flex flex-wrap gap-3">
        <button 
          className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors flex items-center gap-2" 
          onClick={copy}
        >
          📋 Copy Text
        </button>
        <button 
          className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors flex items-center gap-2" 
          onClick={exportAsPDF}
        >
          📄 Export as PDF
        </button>
        <button 
          className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium transition-colors flex items-center gap-2" 
          onClick={exportAsWord}
        >
          📝 Export as Word Doc
        </button>
      </div>
      
      <div className="mt-4">
        <details className="group">
          <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
            Preview content (click to expand)
          </summary>
          <pre className="mt-2 max-h-60 overflow-auto text-xs bg-gray-50 p-3 rounded border">{md}</pre>
        </details>
      </div>
    </div>
  );
}
